// Spec 47 approval-ux — cmp_approval_chain_visualizer / approver_chain_visualizer.
//
// "Compact stepper showing the approval chain with past decisions, current
// step (highlighted with quorum progress if applicable), and upcoming
// steps. Works for serial, parallel, and quorum chains. Live-updates when
// other approvers decide." (spec 47 line 567)
//
// a11y per spec 47:
//   - Stepper is an <ol> with aria-label='Approval chain, {{total}} steps'
//   - Each step is an <li> with aria-current='step' on the current node
//   - Status announced both via icon label AND text (color is never the
//     only signal)

import { forwardRef, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export type ChainStepStatus =
  | "pending"
  | "current"
  | "approved"
  | "rejected"
  | "skipped"
  | "escalated";

export type ChainStepKind = "serial" | "parallel" | "quorum";

export interface ChainQuorum {
  readonly required: number;
  readonly total: number;
  readonly current: number;
}

export interface ChainStep {
  readonly id: string;
  readonly label: string;
  readonly status: ChainStepStatus;
  readonly kind?: ChainStepKind;
  /** Decider name + timestamp for past steps; "due in 6h" for current; null for future. */
  readonly meta?: string;
  /** Required when kind === "quorum". */
  readonly quorum?: ChainQuorum;
  /** Set when status === "escalated" — surfaces the escalation icon. */
  readonly escalationReason?: string;
}

export interface ChainVisualizerProps extends HTMLAttributes<HTMLOListElement> {
  readonly steps: readonly ChainStep[];
  /** Total step count for the aria-label (defaults to steps.length). */
  readonly chainAriaLabel?: string;
  /**
   * Localized step-status wording. The design system carries no i18n runtime,
   * so the consumer supplies the strings; {@link DEFAULT_STATUS_LABEL} is the
   * English fallback. Without this the component printed "Pending"/"Approved"
   * verbatim into whatever locale it was mounted in — the Turkish approval-chain
   * screen showed "Konunun yöneticisi Pending" on every step (ADR-006: every
   * user-facing string is a translation key).
   */
  readonly statusLabels?: Readonly<Partial<Record<ChainStepStatus, string>>>;
  /**
   * Localized chain-kind wording, same reason as {@link statusLabels}. `quorum`
   * is a function because the label embeds two counts and their order is a
   * translation decision, not a formatting one. Under a Turkish locale the
   * untranslated default was additionally uppercased by CSS into "SERİAL" —
   * the dotted capital İ — which is not a word in either language.
   */
  readonly kindLabels?: {
    readonly serial?: string;
    readonly parallel?: string;
    readonly quorum?: (required: number, total: number) => string;
  };
}

const DEFAULT_STATUS_LABEL: Readonly<Record<ChainStepStatus, string>> = {
  pending: "Pending",
  current: "Current",
  approved: "Approved",
  rejected: "Rejected",
  skipped: "Skipped",
  escalated: "Escalated",
};

export const ChainVisualizer = forwardRef<HTMLOListElement, ChainVisualizerProps>(
  ({ steps, chainAriaLabel, statusLabels, kindLabels, className, ...rest }, ref) => {
    const ariaLabel = chainAriaLabel ?? `Approval chain, ${steps.length} steps`;
    const label = (status: ChainStepStatus): string =>
      statusLabels?.[status] ?? DEFAULT_STATUS_LABEL[status];
    return (
      <ol
        ref={ref}
        aria-label={ariaLabel}
        data-component="chain-visualizer"
        className={cx("nai-chain-visualizer", className)}
        {...rest}
      >
        {steps.map((step, idx) => {
          const isCurrent = step.status === "current";
          const isLast = idx === steps.length - 1;
          return (
            <li
              key={step.id}
              data-component="chain-step"
              data-status={step.status}
              aria-current={isCurrent ? "step" : undefined}
              className={cx("nai-chain-step", `nai-chain-step--${step.status}`)}
            >
              <span
                data-component="chain-step-node"
                className="nai-chain-step-node"
                // Status duplicated as visible icon label for screen readers
                // (spec 47 a11y: "Status announced both via icon label and text")
                aria-hidden="true"
              />
              <span className="nai-chain-step-content">
                <span data-component="chain-step-label" className="nai-chain-step-label">
                  {step.label}
                </span>
                <span
                  data-component="chain-step-status-text"
                  className="nai-chain-step-status-text"
                >
                  {label(step.status)}
                </span>
                {step.kind !== undefined && (
                  <span
                    data-component="chain-step-kind-badge"
                    data-kind={step.kind}
                    className="nai-chain-step-kind-badge"
                  >
                    {step.kind === "quorum" && step.quorum !== undefined
                      ? (kindLabels?.quorum?.(step.quorum.required, step.quorum.total) ??
                        `Quorum ${step.quorum.required}/${step.quorum.total}`)
                      : step.kind === "serial"
                        ? (kindLabels?.serial ?? "Serial")
                        : (kindLabels?.parallel ?? "Parallel")}
                  </span>
                )}
                {step.meta !== undefined && (
                  <span data-component="chain-step-meta" className="nai-chain-step-meta">
                    {step.meta}
                  </span>
                )}
                {step.kind === "quorum" && step.quorum !== undefined && (
                  <span
                    data-component="chain-step-quorum-progress"
                    className="nai-chain-step-quorum-progress"
                    role="progressbar"
                    aria-valuenow={step.quorum.current}
                    aria-valuemin={0}
                    aria-valuemax={step.quorum.required}
                    aria-label={`${step.quorum.current} of ${step.quorum.required} needed`}
                  >
                    <span
                      className="nai-chain-step-quorum-progress-fill"
                      style={{
                        // Guard against division-by-zero + negative current:
                        // required=0 yields 0% (nothing needed = "full" is
                        // nonsensical for a progress marker — render empty);
                        // current<0 clamps to 0; current>required clamps to
                        // 100 (the existing Math.min case). This replaces
                        // an earlier computation that produced width="NaN%"
                        // on required=0 and negative widths on current<0 —
                        // both CSS-invalid.
                        width: `${
                          step.quorum.required > 0
                            ? Math.max(
                                0,
                                Math.min(100, (step.quorum.current / step.quorum.required) * 100),
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </span>
                )}
                {step.escalationReason !== undefined && (
                  <span
                    data-component="chain-step-escalation"
                    className="nai-chain-step-escalation"
                    aria-label={`Escalated: ${step.escalationReason}`}
                  >
                    {step.escalationReason}
                  </span>
                )}
              </span>
              {!isLast && (
                <span
                  data-component="chain-connector"
                  className="nai-chain-connector"
                  // dashed for upcoming, solid for past — driven by data-attrs
                  data-style={
                    step.status === "approved" || step.status === "rejected" || step.status === "skipped"
                      ? "solid"
                      : "dashed"
                  }
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    );
  },
);
ChainVisualizer.displayName = "ChainVisualizer";
